// src/pages/admin/AdminBillingPage.tsx
import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CreditCard,
  Zap,
  CheckCircle,
  ArrowLeft,
  Loader,
  AlertTriangle,
  Calendar,
  Shield,
  Users,
  MessageSquare,
  History,
  Image,
} from "lucide-react";
import * as api from "../../services/api";
import type { BillingStatus } from "../../services/api";
import PlanBadge from "../../components/ui/PlanBadge";
import styles from "./AdminBillingPage.module.css";

const AdminBillingPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [toast, setToast] = useState<{ type: "success" | "error"; msg: string } | null>(null);

  // Détecter le retour depuis Stripe
  useEffect(() => {
    if (searchParams.get("success") === "1") {
      setToast({ type: "success", msg: t("admin.billing.upgradeSuccess") });
    } else if (searchParams.get("canceled") === "1") {
      setToast({ type: "error", msg: t("admin.billing.upgradeCanceled") });
    }
  }, [searchParams, t]);

  useEffect(() => {
    api
      .getBillingStatus()
      .then(setStatus)
      .catch(() => setToast({ type: "error", msg: t("admin.billing.loadError") }))
      .finally(() => setLoading(false));
  }, [t]);

  // Fermer le toast après 5s
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);

  const handleUpgrade = async (): Promise<void> => {
    setCheckoutLoading(true);
    try {
      const { url } = await api.createCheckoutSession();
      window.location.href = url;
    } catch {
      setToast({ type: "error", msg: t("admin.billing.checkoutError") });
      setCheckoutLoading(false);
    }
  };

  const handlePortal = async (): Promise<void> => {
    setPortalLoading(true);
    try {
      const { url } = await api.createPortalSession();
      window.location.href = url;
    } catch {
      setToast({ type: "error", msg: t("admin.billing.portalError") });
      setPortalLoading(false);
    }
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString(undefined, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const isTrialing = status?.subscriptionStatus === "trialing";
  const isPastDue = status?.subscriptionStatus === "past_due";
  const isPro = status?.plan === "PRO";

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Loader className="w-8 h-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Toast */}
      {toast && (
        <div className={`${styles.toast} ${toast.type === "success" ? styles.toastSuccess : styles.toastError}`}>
          {toast.type === "success" ? <CheckCircle className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className={styles.header}>
        <Link to="/admin/dashboard" className={styles.backLink}>
          <ArrowLeft className="w-4 h-4" />
          {t("common.back")}
        </Link>
        <div className={styles.headerContent}>
          <CreditCard className="w-6 h-6 text-brand-primary" />
          <h1 className={styles.title}>{t("admin.billing.title")}</h1>
        </div>
      </div>

      <div className={styles.container}>

        {/* ── Statut actuel ── */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h2 className={styles.cardTitle}>{t("admin.billing.currentPlan")}</h2>
            <PlanBadge plan={status?.plan ?? "FREE"} />
          </div>

          {isPro && (
            <div className={styles.statusRow}>
              <div className={styles.statusItem}>
                <Calendar className="w-4 h-4 text-gray-500" />
                <span className={styles.statusLabel}>{t("admin.billing.renewsOn")}</span>
                <span className={styles.statusValue}>{formatDate(status?.currentPeriodEnd ?? null)}</span>
              </div>
              {isTrialing && (
                <div className={styles.statusItem}>
                  <Shield className="w-4 h-4 text-brand-primary" />
                  <span className={styles.statusLabel}>{t("admin.billing.trialEnds")}</span>
                  <span className={styles.statusValue}>{formatDate(status?.trialEndsAt ?? null)}</span>
                </div>
              )}
              {isPastDue && (
                <div className={styles.pastDueBanner}>
                  <AlertTriangle className="w-4 h-4" />
                  {t("admin.billing.pastDue")}
                </div>
              )}
            </div>
          )}

          {isPro ? (
            <button
              onClick={handlePortal}
              disabled={portalLoading}
              className={styles.portalButton}
            >
              {portalLoading ? <Loader className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
              {t("admin.billing.manageSubscription")}
            </button>
          ) : (
            <button
              onClick={handleUpgrade}
              disabled={checkoutLoading}
              className={styles.upgradeButton}
            >
              {checkoutLoading ? <Loader className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              {t("admin.billing.upgradeToPro")}
            </button>
          )}
        </div>

        {/* ── Comparaison plans ── */}
        <div className={styles.plansGrid}>
          {/* Plan Free */}
          <div className={`${styles.planCard} ${!isPro ? styles.planCardActive : ""}`}>
            <div className={styles.planHeader}>
              <h3 className={styles.planName}>Free</h3>
              <p className={styles.planPrice}>0 €<span className={styles.planPeriod}>/mes</span></p>
            </div>
            <ul className={styles.featureList}>
              <FeatureRow icon={<Calendar className="w-4 h-4" />} label={t("admin.billing.feature.events1")} ok />
              <FeatureRow icon={<Users className="w-4 h-4" />} label={t("admin.billing.feature.clients30")} ok />
              <FeatureRow icon={<MessageSquare className="w-4 h-4" />} label={t("admin.billing.feature.whatsapp")} ok={false} />
              <FeatureRow icon={<History className="w-4 h-4" />} label={t("admin.billing.feature.history7")} ok />
              <FeatureRow icon={<Image className="w-4 h-4" />} label={t("admin.billing.feature.branding")} ok={false} />
            </ul>
          </div>

          {/* Plan Pro */}
          <div className={`${styles.planCard} ${styles.planCardPro} ${isPro ? styles.planCardActive : ""}`}>
            <div className={styles.planProBadge}>{t("admin.billing.recommended")}</div>
            <div className={styles.planHeader}>
              <h3 className={styles.planName}>Pro</h3>
              <p className={styles.planPrice}>29 €<span className={styles.planPeriod}>/mes</span></p>
            </div>
            <ul className={styles.featureList}>
              <FeatureRow icon={<Calendar className="w-4 h-4" />} label={t("admin.billing.feature.eventsUnlimited")} ok />
              <FeatureRow icon={<Users className="w-4 h-4" />} label={t("admin.billing.feature.clientsUnlimited")} ok />
              <FeatureRow icon={<MessageSquare className="w-4 h-4" />} label={t("admin.billing.feature.whatsapp")} ok />
              <FeatureRow icon={<History className="w-4 h-4" />} label={t("admin.billing.feature.historyUnlimited")} ok />
              <FeatureRow icon={<Image className="w-4 h-4" />} label={t("admin.billing.feature.branding")} ok />
            </ul>
            {!isPro && (
              <button onClick={handleUpgrade} disabled={checkoutLoading} className={styles.planUpgradeButton}>
                {checkoutLoading ? <Loader className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                {t("admin.billing.startTrial")}
              </button>
            )}
          </div>
        </div>

        <p className={styles.trialNote}>{t("admin.billing.trialNote")}</p>
      </div>
    </div>
  );
};

// Helper inline
const FeatureRow: React.FC<{ icon: React.ReactNode; label: string; ok: boolean }> = ({ icon, label, ok }) => (
  <li className={styles.featureRow}>
    <span className={ok ? styles.featureIconOk : styles.featureIconNo}>
      {ok ? <CheckCircle className="w-4 h-4" /> : <span className={styles.featureCross}>✕</span>}
    </span>
    <span className={styles.featureIcon}>{icon}</span>
    <span className={ok ? styles.featureLabel : styles.featureLabelNo}>{label}</span>
  </li>
);

export default AdminBillingPage;
